import {
  Component,
  computed,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  ViewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatePipe, NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { Popover } from 'primeng/popover';
import { BadgeDirective } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { BellNotificationService } from '@service/notification/bell-notification.service';
import { BellNotification } from '@model/notification/bell-notification.model';
import { getBellNotificationDi } from '@cfg/services/bell-notification-di';

const NOTIFICATION_TYPE_ICONS: Record<string, string> = {
  PENDING_SECOND_SIGNATURE: 'pi-file-check',
  CASE_ASSIGNED: 'pi-user-plus',
  REASSIGNMENT_REQUEST: 'pi-arrow-right-arrow-left',
  CASE_REASSIGNMENT: 'pi-check-circle',
  REASSIGNMENT_REJECTED: 'pi-times-circle',
  MOORE_SEND_SUCCESS: 'pi-cloud-upload',
  MOORE_SEND_ERROR: 'pi-exclamation-triangle',
  SIGNATURE_COMPLETED: 'pi-verified',
  SIGNATURE_CREATION_AUTHORIZATION: 'pi-key',
  REPROCESS_FROM_BIOINFORMATICS: 'pi-refresh'
};

@Component({
  selector: 'app-bell-notification',
  standalone: true,
  imports: [NgClass, DatePipe, Popover, BadgeDirective, ButtonModule, TooltipModule, TranslateModule],
  providers: [getBellNotificationDi],
  templateUrl: './bell-notification.component.html',
  styleUrls: ['./bell-notification.component.scss']
})
export class BellNotificationComponent implements OnInit, OnDestroy {
  private readonly bellNotificationService = inject(BellNotificationService);
  private readonly router = inject(Router);

  @ViewChild('notificationPanel') notificationPanel!: Popover;

  readonly notifications: Signal<BellNotification[]> = toSignal(this.bellNotificationService.notifications$, { initialValue: [] });
  readonly unreadCount: Signal<number> = toSignal(this.bellNotificationService.unreadCount$, { initialValue: 0 });
  readonly hasUnread = computed(() => this.unreadCount() > 0);

  ngOnInit(): void {
    this.bellNotificationService.startPolling();
  }

  ngOnDestroy(): void {
    this.bellNotificationService.stopPolling();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.hidden) {
      this.bellNotificationService.stopPolling();
    } else {
      this.bellNotificationService.startPolling();
    }
  }

  togglePanel(event: Event): void {
    if (!this.notificationPanel.overlayVisible) {
      this.bellNotificationService.loadNotifications();
    }
    this.notificationPanel.toggle(event);
  }

  getTypeIcon(type: string): string {
    return NOTIFICATION_TYPE_ICONS[type] ?? 'pi-info-circle';
  }

  onNotificationClick(notification: BellNotification): void {
    this.notificationPanel.hide();
    if (
      notification.resourceUrl &&
      this.isSafeInternalRoute(notification.resourceUrl)
    ) {
      void this.router.navigateByUrl(notification.resourceUrl);
    }
  }

  private isSafeInternalRoute(url: string): boolean {
    return url.startsWith('/') && !url.includes('//') && !/^[a-z]+:/i.test(url);
  }

  onMarkOneAsRead(id: string, event: Event): void {
    event.stopPropagation();
    this.bellNotificationService.markAsRead(id);
  }

  onMarkAllAsRead(): void {
    this.bellNotificationService.markAllAsRead();
  }
}
